import { type HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import type { QueryClient } from '@tanstack/react-query';

import type { History } from 'history';

import { trackEvent } from 'analytics/appInsights';
import { getAccessToken } from 'api/authentication/utils/getAccessToken';
import { QUERY_KEYS } from 'api/constants';
import type { DataApiClient, DefaultCasedProjectNode, NodeDeletionResult, ProjectNode } from 'api/data/client-dataapi';
import { normalizeProposalId } from 'api/data/events/normalizeProposalId';
import type {
  DataNodeCreatedEvent,
  DataNodeUpdatedEvent,
  DataRelationCreatedEvent,
  DataRelationDeletedEvent
} from 'api/data/events/signalREvents';
import { nodeDetailQueryKeys } from 'api/data/hooks/nodeDetailQueryKeys';
import type { DefaultCasedOwnershipMapping, OwnershipMapping } from 'api/data/ownership/models/OwnershipMapping';
import type { OwnershipUser } from 'api/data/ownership/models/OwnershipUser';
import type { ReportRun } from 'api/data/reports/reportModels';
import type { TreeSyncContext } from 'api/decomposition-tree/treeSyncContext';
import type { DefaultCasedOutputOperationResult } from 'api/outputOperationResult';
import { invalidateProjectGraphNodeFromEvent } from 'api/project-graph/events/invalidateProjectGraphNode';
import { invalidateDeletedNodeDetails, syncNodeDetailFromEvent } from 'features/node-details/model/nodeDetailLiveSync';
import {
  handleNodeCreatedEvent,
  handleRelationCreatedEvent,
  handleRelationDeletedEvent
} from 'features/tree-view/model/coordinateSignalREvents';
import { onDataNodeUpdated } from 'features/tree-view/model/treeLiveSync';
import { DELETE_NODE_STATE } from 'state/deleteNodeState/deleteNodeState';
import type { GridActions } from 'state/grids/gridActions';
import { GRID_STORE } from 'state/grids/gridState';
import { useProjectNodePropertyUpdatesStore } from 'state/grids/store/useProjectNodePropertyUpdatesStore';
import { getSemNodeProperties } from 'state/grids/utils/getSemNodeProperties';
import { updateRootRows } from 'state/grids/utils/rowActionsUtils';
import {
  getProposalContextFromPathname,
  shouldProcessEvent,
  shouldProcessEventForCurrentRoute
} from 'state/grids/utils/shouldProcessEvent';
import { IMPORTS_IN_PROGRESS_STORE, reconcileImportsAfterReconnect } from 'state/imports/importsInProgress';
import { PENDING_EDITS_STATE } from 'state/pendingEdits/pendingEditsState';
import { PROJECT_TEAM_MAPPINGS } from 'state/projectTeamMappingState/projectTeamMappingState';
import { PROJECTS_STORE, type ProjectOperationProgress } from 'state/projects/projectsStore';
import type { ReleaseTemplateWizardActions } from 'state/releaseTemplateWizard/releaseTemplateWizardActions';
import { SELECTED_PROJECT_STORE } from 'state/selectedProject/selectedProjectState';
import type { SignalRActions } from 'state/signalr/SignalRActions';
import { SNACKBAR_STORE } from 'state/snackbar/snackbarStore';
import type { SemNode } from 'types/data/semNode';
import { devLog } from 'utils/dev-utils';
import { hasValue } from 'utils/values';

import type { RequestResultSetUpdateInstruction } from '../data/dataActions';
import { getResultSetEntry } from '../grids/getResultSetEntry';
import { type HubConnectionManagerConfig, createHubConnectionManager } from './hubConnectionManager';
import type {
  AddNodeResult,
  DataNodeDeletedEvent,
  DataNodePropertyUpdatedEvent,
  EventCompletion,
  HyperRelationshipDeletionBlockedModel,
  ImportCompletedNotification,
  ImportStartedNotification,
  NodeMovedInDecompositionEvent,
  ProjectDeletedEvent,
  ProjectUpdatedEvent,
  RelationshipsChangedEvent,
  RootNodeAddedEvent,
  SelectionMovedEvent,
  TemplateReleasedEvent
} from './signalrState';

const notificationHubConnectionManager = createHubConnectionManager();

/**
 * Connects to NotificationApi's `/notifications` hub. DataApi's `DataHubContext` now publishes
 * effectively every server-push notification (project/template/import/report lifecycle events,
 * grid data changes, etc.) through NotificationApi instead of broadcasting them directly off
 * DataApi's own `/datahub` connection - see `Core/Data/src/DataApi/Hubs/DataHubContext.cs`. Only
 * request/response-style hub calls (`RequestRootRows`, `MovePropertySelection`, ...) - things that
 * reply directly to the caller's own invoke - stay on `SignalRActions`/`/datahub`.
 *
 * Mirrors SignalRActions' connect/retry pattern, but simplified: NotificationApi's hub uses the
 * default JSON protocol (no MessagePack), and there's no snackbar UX here to avoid two independent
 * "Reconnecting..." messages fighting with the main connection's.
 */
export class NotificationApiActions {
  private subscriberId: string;
  private secureUrl: string;
  private userEmail: string;

  private gridActions: GridActions;
  private dataApiClient: DataApiClient;
  private history: History;
  private releaseTemplateWizardActions: ReleaseTemplateWizardActions;
  private queryClient: QueryClient;
  // requestSemanticUpdates/updateRootRows/RequestRootRows still call back into the DataHub
  // connection to fetch the actual refreshed data - only the completion event itself has moved to
  // NotificationApi, so we need a live SignalRActions instance to hand them.
  private signalRActions: SignalRActions;

  constructor(
    subscriberId: string,
    userEmail: string,
    secureUrl: string,
    gridActions: GridActions,
    dataApiClient: DataApiClient,
    history: History,
    releaseTemplateWizardActions: ReleaseTemplateWizardActions,
    queryClient: QueryClient,
    signalRActions: SignalRActions
  ) {
    this.subscriberId = subscriberId;
    this.userEmail = userEmail;
    this.secureUrl = secureUrl;
    this.gridActions = gridActions;
    this.dataApiClient = dataApiClient;
    this.history = history;
    this.releaseTemplateWizardActions = releaseTemplateWizardActions;
    this.queryClient = queryClient;
    this.signalRActions = signalRActions;
  }

  /** Shared by every tree live-sync handler below. */
  private treeSyncContext(): TreeSyncContext {
    return {
      queryClient: this.queryClient,
      pathname: this.history.location.pathname
    };
  }

  private hubConfig(): HubConnectionManagerConfig {
    return {
      buildConnection: () =>
        new HubConnectionBuilder()
          .withUrl(this.secureUrl, { accessTokenFactory: () => getAccessToken() })
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Critical)
          .build(),
      bindEvents: (connection) => this.BindEvents(connection),
      onUnexpectedClose: (error) => {
        if (hasValue(error)) {
          devLog('NotificationApi connection closed unexpectedly:', String(error));
        }
      },
      // No snackbar hooks here (unlike SignalRActions) - see the class doc comment for why. We still
      // reconcile imports on reconnect/restart though: ImportStarted/ImportCompleted arrive on this
      // connection now, so a drop here can miss one the same way a drop on SignalRActions used to.
      onReconnected: () => reconcileImportsAfterReconnect(this.queryClient),
      onRestarted: () => reconcileImportsAfterReconnect(this.queryClient)
    };
  }

  public MoveSelection(model: SelectionMovedEvent) {
    return this.invoke('MoveSelection', model);
  }

  private async invoke<T>(methodName: string, model: T) {
    const connection = await this.Connect();
    connection.invoke(methodName, model);
  }

  public async on(eventName: string, callback: (...args: any[]) => void): Promise<void> {
    return notificationHubConnectionManager.on(this.subscriberId, this.hubConfig(), eventName, callback);
  }

  public async off(eventName: string, callback: (...args: any[]) => void): Promise<void> {
    return notificationHubConnectionManager.off(this.subscriberId, this.hubConfig(), eventName, callback);
  }

  async Connect(retryInSeconds = 3): Promise<HubConnection> {
    return notificationHubConnectionManager.connect(this.subscriberId, { ...this.hubConfig(), retryInSeconds });
  }

  async Disconnect() {
    return notificationHubConnectionManager.disconnect(this.subscriberId, {
      onDisconnectError: (e) => console.error(`Error while disconnecting from NotificationApi: ${e}`)
    });
  }

  private BindEvents(hubConnect: HubConnection) {
    // Connection lifecycle (onclose/restart) is handled by hubConnectionManager - see hubConfig() above.

    hubConnect.on('SelectionMoved', (event: SelectionMovedEvent) => {
      if (this.userEmail !== event.User) {
        GRID_STORE.getState().selectCell(event.User, event.ResultSetId, event.ColumnIndex, event.RowIndex);
      }
    });

    hubConnect.on('ImportStarted', (event: ImportStartedNotification) => {
      if (event.IsDryRun) {
        return;
      }
      IMPORTS_IN_PROGRESS_STORE.getState().setImportStarted(event);
    });

    hubConnect.on('ImportCompleted', (event: ImportCompletedNotification) => {
      if (event.IsDryRun) {
        return;
      }
      IMPORTS_IN_PROGRESS_STORE.getState().setImportCompleted(event.ImportRunId);
    });

    hubConnect.on('ProjectUpdateProgress', (projectUpdateProgress: ProjectOperationProgress) => {
      PROJECTS_STORE.getState().updateOperationProgress(projectUpdateProgress);
    });

    hubConnect.on(
      'ProjectUpdated',
      async (
        event: EventCompletion<ProjectUpdatedEvent, DefaultCasedOutputOperationResult<DefaultCasedProjectNode>>
      ) => {
        // a project update is completed
        if (event.Result.Success) {
          const { Id, TemplateId, Name, ProjectTeamId, IsLocked } = event.Result.Result;

          const projectNode: ProjectNode = {
            id: Id,
            name: Name,
            templateId: TemplateId,
            projectTeamId: ProjectTeamId,
            isLocked: !!IsLocked
          };

          PROJECTS_STORE.getState().updateProject(projectNode);
          PROJECTS_STORE.getState().removeBlockingOperation(event.DataEvent.EventId);

          if (SELECTED_PROJECT_STORE.getState().projectId === Id) {
            this.dataApiClient
              .GetUserSpecificTemplate(projectNode.id)
              .then((template) => {
                if (!template.success) {
                  console.warn(`Unable to load template '${TemplateId} for project '${Id}': '${template.error}' `);
                  return;
                }

                SELECTED_PROJECT_STORE.getState().setProjectWithTemplate(projectNode, template.result);
              })
              .catch((reason) => {
                console.warn(`Unable to load template '${TemplateId} for project '${Id}': '${reason}' `);
              });
          }
        } else {
          PROJECTS_STORE.getState().removeBlockingOperation(event.DataEvent.EventId);
        }
      }
    );

    hubConnect.on(
      'ProjectDeleted',
      (event: EventCompletion<ProjectDeletedEvent, DefaultCasedOutputOperationResult<DefaultCasedProjectNode>>) => {
        if (!event.Result.Success) {
          trackEvent('Error on ProjectDeleted stream', 'Project', { error: event.Result.Error });
          // show snackbar only to the user who requested the operation
          if (this.userEmail === event.DataEvent.UserEmail) {
            SNACKBAR_STORE.getState().showSnackbar(
              'Error deleting project. Please try again or contact support.',
              'error',
              true
            );
          }
          return;
        }

        PROJECTS_STORE.getState().removeProject(event.DataEvent.ProjectId);
        this.queryClient.removeQueries({
          queryKey: [QUERY_KEYS.OWNERSHIP_MAPPINGS, event.DataEvent.ProjectId]
        });

        if (this.history.location.pathname.includes(event.Result.Result.Id)) {
          this.history.push(`/projects`);
        }

        // The deleted project may have been granted to integration clients; refetch the list
        // so it no longer appears under any client's project access.
        this.queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTEGRATION_CLIENTS] });
      }
    );

    hubConnect.on(
      'NodePropertiesUpdated',
      async (event: EventCompletion<DataNodePropertyUpdatedEvent, DefaultCasedOutputOperationResult<SemNode[]>>) => {
        event.DataEvent.ProposalId = normalizeProposalId(event.DataEvent.ProposalId);

        const { projectId, updateProperties } = GRID_STORE.getState();
        if (projectId !== event.DataEvent.ProjectId) {
          return;
        }

        const { currentProposalId, key } = getProposalContextFromPathname(this.history.location.pathname);
        if (
          !shouldProcessEvent({
            gridSpecificationKey: key,
            currentProposalId,
            eventProposalId: event.DataEvent.ProposalId
          })
        ) {
          return;
        }

        if (!event.Result.Success) {
          trackEvent('Error on NodePropertiesUpdated stream', 'Project', { error: event.Result.Error });
          // show snackbar only to the user who requested the operation
          if (this.userEmail === event.DataEvent.UserEmail) {
            SNACKBAR_STORE.getState().showSnackbar(
              'Error syncing changes. Please refresh and try again. Contact support if problem persists.',
              'error',
              true
            );

            // Roll back to the old value
            const updateObject = useProjectNodePropertyUpdatesStore
              .getState()
              .updates.find(
                (update) =>
                  !update.hasRolledBack &&
                  update.projectId === event.DataEvent.ProjectId &&
                  update.nodeId === event.DataEvent.NodeId &&
                  update.propertyId === event.DataEvent.PropertyId
              );

            if (updateObject) {
              const properties = GRID_STORE.getState().properties[updateObject.nodeId];
              if (properties) {
                useProjectNodePropertyUpdatesStore.getState().addUpdates({
                  ...updateObject,
                  // Flag it as rolled back to trigger PropertyValueCell to reset the display value
                  hasRolledBack: true
                });
                await updateProperties(
                  updateObject.nodeId,
                  {
                    ...properties,
                    [updateObject.propertyId]: updateObject.currentValue
                  },
                  { recalculateCellSizes: true }
                );
              }
            }
          }
          return;
        }

        await Promise.all(
          event.Result.Result.map((node) =>
            updateProperties(node.Id, getSemNodeProperties(currentProposalId, node.Properties), {
              recalculateCellSizes: true
            })
          )
        );

        // Keep the cached single-node detail (node detail panel / relation popup) in sync with the
        // live update so a later panel open refetches these values instead of re-pushing a stale
        // snapshot into the grid store. Prefix match covers every proposal/template variant.
        event.Result.Result.forEach((node) =>
          this.queryClient.invalidateQueries({
            queryKey: nodeDetailQueryKeys.node(event.DataEvent.ProjectId, node.Id)
          })
        );
      }
    );

    hubConnect.on(
      'RelationshipsChanged',
      (event: EventCompletion<RelationshipsChangedEvent, DefaultCasedOutputOperationResult<string[]>>) => {
        event.DataEvent.ProposalId = normalizeProposalId(event.DataEvent.ProposalId);

        if (GRID_STORE.getState().projectId !== event.DataEvent.ProjectId) {
          return;
        }

        if (!shouldProcessEventForCurrentRoute(this.history.location.pathname, event.DataEvent.ProposalId)) {
          return;
        }

        if (!event.Result.Success) {
          PENDING_EDITS_STATE.getState().removePendingRelationshipChange(event.DataEvent.EventId);
          trackEvent('Error on RelationshipsChanged stream', 'Project', { error: event.Result.Error });
          // show snackbar only to the user who requested the add
          if (this.userEmail === event.DataEvent.UserEmail) {
            SNACKBAR_STORE.getState().showSnackbar(
              'Error saving relationship changes. Please try again or contact support.',
              'error',
              true
            );
          }
          return;
        }
        const nodesToConnect = event.DataEvent.NodesToConnect ?? [];
        this.gridActions.requestSemanticUpdates(
          this.signalRActions,
          [...nodesToConnect, event.DataEvent.ParentDataNodeId],
          [],
          {
            connectedNodeIds: [...nodesToConnect, ...event.Result.Result],
            originatingResultSetId: event.DataEvent.ResultSetId,
            queryNodeId: event.DataEvent.QueryNodeId,
            parentNodeId: event.DataEvent.ParentDataNodeId,
            pendingEventId: event.DataEvent.EventId
          }
        );
      }
    );

    hubConnect.on('HyperRelationshipDeletionBlocked', (model: HyperRelationshipDeletionBlockedModel) => {
      const dataNodeId = model.IdsOfHyperNodeBeingBlocked[0];
      if (!dataNodeId) {
        return;
      }

      this.gridActions
        .DeleteNode(
          model.ProjectId,
          model.TemplateNodeId,
          dataNodeId,
          model.QueryNodeId,
          model.ResultSetId,
          {},
          {},
          undefined
        )
        .then(() => {})
        .finally(() => {
          PENDING_EDITS_STATE.getState().removePendingRelationshipChange(model.EventId);
        });
    });

    hubConnect.on(
      'NodeMovedInDecomposition',
      (event: EventCompletion<NodeMovedInDecompositionEvent, DefaultCasedOutputOperationResult<string[]>>) => {
        if (GRID_STORE.getState().projectId !== event.DataEvent.ProjectId) {
          return;
        }

        if (!event.Result.Success) {
          trackEvent('Error on NodeMovedInDecomposition stream', 'Project', { error: event.Result.Error });
          // show snackbar only to the user who requested the operation
          if (this.userEmail === event.DataEvent.UserEmail) {
            SNACKBAR_STORE.getState().showSnackbar(
              'Error changing hierarchy. Please refresh and try again.  Contact support if problem persists.',
              'error',
              true
            );
            //todo: hierachy view is optimistic, we need a roll back mechanism
          }
          return;
        }

        this.gridActions.requestSemanticUpdates(
          this.signalRActions,
          [event.DataEvent.DataNodeId, event.DataEvent.NextParentId, event.DataEvent.PreviousParentId],
          []
        );
      }
    );

    hubConnect.on(
      'RootRowAdded',
      (event: EventCompletion<RootNodeAddedEvent, DefaultCasedOutputOperationResult<AddNodeResult>>) => {
        event.DataEvent.ProposalId = normalizeProposalId(event.DataEvent.ProposalId);

        if (GRID_STORE.getState().projectId !== event.DataEvent.ProjectId) {
          return;
        }

        if (!event.Result.Success) {
          PENDING_EDITS_STATE.getState().removePendingRootNodeCreation(event.DataEvent.EventId);
          trackEvent('Error on RootRowAdded stream', 'Project', { error: event.Result.Error });
          // show snackbar only to the user who requested the operation
          if (this.userEmail === event.DataEvent.UserEmail) {
            SNACKBAR_STORE.getState().showSnackbar(
              'Error creating a data instance. Please try again or contact support.',
              'error',
              true
            );
          }
          return;
        }

        if (!shouldProcessEventForCurrentRoute(this.history.location.pathname, event.DataEvent.ProposalId)) {
          return;
        }

        const addRootNodeResult = event.Result;

        PENDING_EDITS_STATE.getState().setPendingRootNodeCreationNodeId(
          event.DataEvent.EventId,
          addRootNodeResult.Result.Id
        );
        GRID_STORE.getState().addProperties(addRootNodeResult.Result.Id, addRootNodeResult.Result.Properties);

        const { gridSpecifications, filters, propertyFilters, sortings, completedResultSets } = GRID_STORE.getState();

        const resultSetUpdateInstructions: RequestResultSetUpdateInstruction[] = [];

        Object.keys(gridSpecifications).forEach((key) => {
          if (!completedResultSets[key] || !filters[key] || !propertyFilters[key] || !sortings[key]) {
            return;
          }

          const { proposalId: currentProposalId, queryId, dataNodeId } = getResultSetEntry(key);

          if (dataNodeId) {
            // this a detail grid, determine if it needs updating
            // detailgrids only have one rootNode (for now), the Id of the rootNode can never be the same as the newly created node.
            // there are no scenario's in which the detailnode should be refreshed
            return;
          }

          if (
            !shouldProcessEvent({
              gridSpecificationKey: key,
              currentProposalId: currentProposalId,
              eventProposalId: event.DataEvent.ProposalId
            })
          ) {
            return;
          }

          resultSetUpdateInstructions.push({
            filters: filters[key],
            propertyFilters: propertyFilters[key],
            sortings: sortings[key],
            projectId: event.DataEvent.ProjectId,
            proposalId: currentProposalId,
            queryId: queryId,
            resultSetId: key,
            rootNodesToRefresh: [event.Result.Result.Id]
          });
        });

        updateRootRows(this.signalRActions, resultSetUpdateInstructions, event.DataEvent.EventId);
      }
    );

    hubConnect.on(
      'DataNodeDeleted',
      (event: EventCompletion<DataNodeDeletedEvent, DefaultCasedOutputOperationResult<NodeDeletionResult>>) => {
        event.DataEvent.ProposalId = normalizeProposalId(event.DataEvent.ProposalId);

        // Gate on the selected project, NOT on GRID_STORE.projectId: that one is only ever populated
        // by the legacy node-query page (via useOverviewSpecification -> initializeDataSet), so any
        // route that deletes without having opened a query grid first — e.g. the project nodes grid —
        // would have its completion event dropped here, leaving the deletion permanently "in progress".
        // SELECTED_PROJECT_STORE is set by SelectedProjectPageWrapper, which wraps every project route.
        if (SELECTED_PROJECT_STORE.getState().projectId !== event.DataEvent.ProjectId) {
          return;
        }

        // The details panel can be open on any route (e.g. Audit), so this runs before the route gate.
        invalidateDeletedNodeDetails(event, this.queryClient);

        if (!shouldProcessEventForCurrentRoute(this.history.location.pathname, event.DataEvent.ProposalId)) {
          return;
        }

        DELETE_NODE_STATE.getState().processDeletionResult(event);

        if (!event.Result.Success) {
          PENDING_EDITS_STATE.getState().removePendingRootNodeCreation(event.DataEvent.EventId);
          trackEvent('Error on DataNodeDeleted stream', 'Project', { error: event.Result.Error });
          // processDeletionResult will show a snackbar to user, no need to show again
          return;
        }

        const involvedNodeIds = Object.keys(event.Result.Result.IsNodeMap).filter(
          (objectKey) => event.Result.Result.IsNodeMap[objectKey]
        );

        this.gridActions.requestSemanticUpdates(this.signalRActions, involvedNodeIds, [], {
          connectedNodeIds: involvedNodeIds,
          originatingResultSetId: event.DataEvent.ResultSetId,
          queryNodeId: event.DataEvent.QueryNodeId,
          pendingEventId: event.DataEvent.EventId
        });
      }
    );

    hubConnect.on('ProjectTeamMappingDeleted', (event: DefaultCasedOutputOperationResult<string>) => {
      PROJECT_TEAM_MAPPINGS.getState().delete(event.Result);
    });

    hubConnect.on(
      'OwnershipMappingUpdated',
      (response: DefaultCasedOutputOperationResult<DefaultCasedOwnershipMapping>) => {
        if (
          response.Success &&
          !!response.Result &&
          response.Result.ProjectId === SELECTED_PROJECT_STORE.getState().projectId
        ) {
          const mapping: OwnershipMapping = {
            projectId: response.Result.ProjectId,
            users: response.Result.Users.map(
              (u): OwnershipUser => ({
                id: u.Id,
                firstName: u.FirstName,
                lastName: u.LastName,
                userName: u.UserName,
                avatar: u.Avatar,
                roles: u.Roles
              })
            )
          };

          this.queryClient.setQueryData([QUERY_KEYS.OWNERSHIP_MAPPINGS, response.Result.ProjectId], mapping);
        }
      }
    );

    hubConnect.on('TemplateBranchCreated', async () => {
      // TODO templates-performance: Update cache instead of invalidating
      // We have the new template data in the event, we could add it to the cache
      this.queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATES] });
    });

    hubConnect.on('TemplateRemoved', async () => {
      // TODO templates-performance: Update cache by removing the template instead of invalidating
      // We have the templateId in the event, we could remove it from the cache
      this.queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATES] });
    });

    hubConnect.on('TemplateReleaseCompleted', async (event: TemplateReleasedEvent) => {
      await this.releaseTemplateWizardActions.handleTemplateReleased(event.TemplateId, event.Success, event.Messages);
      if (event.Success) {
        // TODO templates-performance: Update cache by setting releasedOn instead of invalidating
        // We have the templateId and success status, we could update the releasedOn in the cache
        this.queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATES] });
        this.queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATES, event.TemplateId] });
      }
    });

    // Reports
    //
    // event is typed as the real ReportRun DTO, not narrowed/reshaped: this hub uses Rebus's default
    // JSON serialization (no string-enum converter configured anywhere in that pipeline, on either
    // side), so Status/RunPhase arrive as their plain numeric values - already exactly EReportRunStatus/
    // ERunPhase, since the TS enums below were deliberately given the same numeric values as their C#
    // counterparts (see api/data/reports/reportModels.ts). No remapping needed.
    hubConnect.on('ReportRunUpdated', (event: ReportRun) => {
      const selectedProjectId = SELECTED_PROJECT_STORE.getState().projectId;
      if (event.ProjectId !== selectedProjectId) {
        return;
      }

      this.queryClient.setQueryData<ReportRun[]>([QUERY_KEYS.REPORT_RUNS, event.ProjectId], (runs = []) => {
        const index = runs.findIndex((r) => r.Id === event.Id);
        if (index > -1) {
          const next = runs.slice();
          next[index] = event;
          return next;
        }
        return [...runs, event];
      });
    });

    hubConnect.on('DataNodeCreated', (event: DataNodeCreatedEvent) => {
      event.ProposalId = normalizeProposalId(event.ProposalId);
      const shouldProcess = shouldProcessEventForCurrentRoute(this.history.location.pathname, event.ProposalId);
      if (!shouldProcess) {
        return;
      }

      handleNodeCreatedEvent(event, this.treeSyncContext());
    });

    hubConnect.on('DataRelationCreated', (event: DataRelationCreatedEvent) => {
      event.ProposalId = normalizeProposalId(event.ProposalId);
      const shouldProcess = shouldProcessEventForCurrentRoute(this.history.location.pathname, event.ProposalId);
      if (!shouldProcess) {
        return;
      }

      handleRelationCreatedEvent(event, this.treeSyncContext());
    });

    hubConnect.on('DataRelationDeleted', (event: DataRelationDeletedEvent) => {
      event.ProposalId = normalizeProposalId(event.ProposalId);
      const shouldProcess = shouldProcessEventForCurrentRoute(this.history.location.pathname, event.ProposalId);
      if (!shouldProcess) {
        return;
      }

      handleRelationDeletedEvent(event, this.treeSyncContext());
    });

    hubConnect.on('DataNodeUpdated', (event: DataNodeUpdatedEvent) => {
      event.ProposalId = normalizeProposalId(event.ProposalId);

      // The panel sync targets its own cached variants (live + each open proposal), so it must run
      // regardless of the current route — the route gate below only governs the tree path.
      syncNodeDetailFromEvent(event, this.queryClient);
      // Same reasoning: the graph pane can be open regardless of route, so its own cache invalidation
      // runs unconditionally too.
      invalidateProjectGraphNodeFromEvent(this.queryClient, event);

      if (!shouldProcessEventForCurrentRoute(this.history.location.pathname, event.ProposalId)) {
        return;
      }
      onDataNodeUpdated(event, this.treeSyncContext());
    });
  }
}
